import React, { useState } from 'react';
import Header from './components/Header';
import SimulationForm from './components/SimulationForm';
import Results from './components/Results';
import CostChart from './components/CostChart';

function App() {
  // État pour stocker les résultats de la simulation et le graphique
  const [simulationResult, setSimulationResult] = useState(null);
  const [chartData, setChartData] = useState(null);

  const runSimulation = (params) => {
    // Parsing des paramètres
    const prixAchat = parseFloat(params.prixAchat);
    const apport = parseFloat(params.apport);
    const tauxInteret = parseFloat(params.tauxInteret);
    const dureePret = parseFloat(params.dureePret);
    const fraisNotaire = parseFloat(params.fraisNotaire);
    const loyer = parseFloat(params.loyer);
    const tauxAugmentation = parseFloat(params.tauxAugmentation);
    const chargesCopro = parseFloat(params.chargesCopro);
    const taxeFonciere = parseFloat(params.taxeFonciere);

    // Calcul du montant emprunté et du paiement mensuel (formule standard)
    const principal = prixAchat - apport;
    const monthlyRate = tauxInteret / 100 / 12;
    const totalPayments = dureePret * 12;
    let monthlyPayment = 0;
    if (monthlyRate === 0) {
      monthlyPayment = principal / totalPayments;
    } else {
      monthlyPayment = principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -totalPayments));
    }

    // Simulation sur la durée du crédit (en mois)
    const simulationMonths = totalPayments;
    let cumulativeBuy = [];
    let cumulativeRent = [];
    let labels = [];

    // On considère que les frais de notaire sont payés une fois au départ
    cumulativeBuy[0] = fraisNotaire;
    cumulativeRent[0] = 0;
    labels.push(0);

    let breakEvenMonth = null;
    let remainingPrincipal = principal;

    // Simulation mois par mois avec amortissement du prêt (seul l'intérêt est considéré comme coût, le remboursement du capital construit l'équité)
    for (let month = 1; month <= simulationMonths; month++) {
      let monthlyCost = 0;
      if (month <= totalPayments) {
        const interestPayment = remainingPrincipal * monthlyRate;
        const principalRepayment = monthlyPayment - interestPayment;
        remainingPrincipal -= principalRepayment;
        monthlyCost = interestPayment;
      } else {
        monthlyCost = 0;
      }
      // Ajout mensuel des charges de copropriété (annuelles réparties sur 12 mois)
      monthlyCost += chargesCopro / 12;
      // Ajout mensuel de la taxe foncière (taxe annuelle répartie sur 12 mois)
      monthlyCost += taxeFonciere / 12;
      cumulativeBuy[month] = cumulativeBuy[month - 1] + monthlyCost;

      // Coût mensuel en location prenant en compte l'augmentation annuelle
      const yearsPassed = Math.floor((month - 1) / 12);
      const currentMonthlyRent = loyer * Math.pow(1 + tauxAugmentation / 100, yearsPassed);
      cumulativeRent[month] = cumulativeRent[month - 1] + currentMonthlyRent;

      labels.push((month / 12).toFixed(1));

      // On détermine le break-even dès que le coût cumulé de la location dépasse celui de l'achat
      if (breakEvenMonth === null && cumulativeBuy[month] < cumulativeRent[month]) {
        breakEvenMonth = month;
      }
    }

    setSimulationResult({
      monthlyPayment,
      breakEvenMonth
    });

    // Préparation des données pour le graphique
    setChartData({
      labels,
      datasets: [
        {
          label: "Coût cumulé d'achat",
          data: cumulativeBuy,
          fill: false,
          backgroundColor: "rgba(34, 197, 94, 0.5)", // vert
          borderColor: "rgba(34, 197, 94, 1)"
        },
        {
          label: "Coût cumulé de location",
          data: cumulativeRent,
          fill: false,
          backgroundColor: "rgba(239, 68, 68, 0.5)", // rouge
          borderColor: "rgba(239, 68, 68, 1)"
        }
      ]
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="container mx-auto p-4">
        <SimulationForm runSimulation={runSimulation} />
        {simulationResult && <Results result={simulationResult} />}
        {chartData && <CostChart chartData={chartData} />}
      </div>
    </div>
  );
}

export default App; 